import React, { useEffect, useMemo, useState } from "react";
import classes from "../Management.module.scss";
import styles from "./DealerPipeline.module.scss";
import {
  getDealerInvoiceDefaults,
  createDealerAdminInvoice,
  updateDealerAdminInvoice,
  deleteDealerAdminInvoice,
  sendDealerAdminInvoice,
  getDealerAdminInvoicePdf,
  updateDealerAdminPurchase,
  openPdf,
  formatUsd,
  INVOICE_KIND_LABELS,
} from "../../../services/dealerGarage";
import QuoteFields from "./QuoteFields";
import { errorMessage, parseMoney, ORDER_CONFIRMED_NOTE } from "./dealerAdmin";

const blankLine = () => ({ description: "", quantity: "1", unitPrice: "" });

// One-click lines for the extras that kept getting left off invoices. Amounts
// mirror FEES in Backend/app/utils/dealerPricing.js; 0 means staff fill it in.
const QUICK_LINES = [
  { label: "US title", description: "US title", unitPrice: 450 },
  { label: "Japan storage (month)", description: "Japan yard storage (per month)", unitPrice: 85 },
  { label: "Home delivery", description: "Home delivery", unitPrice: 0 },
  { label: "Duty adjustment", description: "US import duty adjustment", unitPrice: 0 },
];

const toDraftLines = (lines) =>
  (lines || []).map((l) => ({
    description: l.description || "",
    quantity: String(l.quantity === undefined ? 1 : l.quantity),
    unitPrice: l.unitPrice === undefined || l.unitPrice === null ? "" : String(l.unitPrice),
    // The parts printed under the line (the customs breakdown of "Import and
    // duty"); the line's price is their sum.
    breakdown: (l.breakdown || []).map((b) => ({ label: b.label || "", amount: String(b.amount) })),
  }));

const round2 = (n) => Math.round(n * 100) / 100;

const breakdownSum = (parts) =>
  round2(parts.reduce((sum, b) => {
    const n = parseMoney(b.amount);
    return sum + (Number.isFinite(n) && n !== null ? n : 0);
  }, 0));

/**
 * Create or edit one invoice, inline inside a BillingPanel.
 *
 * source 'site': a draft built from line items - pre-filled from
 * /dealer/admin/invoices/defaults (the website's quote formula) - that staff
 * edit, preview as PDF and send. Sending numbers it, freezes it and emails the
 * PDF with the wire / ACH instructions.
 *
 * source 'quickbooks': a record of an invoice issued from QuickBooks (deposits,
 * cheaper cars) - its number and amount - so it shows in the customer's garage
 * and payments can be recorded against it.
 *
 * Props:
 *   invoice    existing invoice to edit, or null for a new one
 *   source     'site' | 'quickbooks' for a new one
 *   kind       default kind for a new one
 *   scope      { purchaseId?, inspectionId?, userId? } the invoice belongs to
 *   onDone(invoice|null)  closes the editor; the panel reloads
 *   onSaleChanged()       the sale's quote was changed from here; its panel reloads
 */
const InvoiceEditor = ({ invoice, source: newSource, kind: newKind, scope, onDone, onSaleChanged }) => {
  const source = invoice ? invoice.source : newSource;
  const isQuickBooks = source === "quickbooks";
  const [loading, setLoading] = useState(!invoice && !isQuickBooks);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(invoice || null);
  // Which slice of the car's cost a new car invoice bills (all, car only,
  // shipping, arrival, duty adjustment), and the choices the server offers.
  const [template, setTemplate] = useState("all");
  const [templates, setTemplates] = useState(null);
  // The sale's agreed price the suggested lines were built to, if any.
  const [agreed, setAgreed] = useState(null);
  const [feePresets, setFeePresets] = useState([]);
  // The customer's deposit balance and how much of it comes off when sent.
  const [depositBalance, setDepositBalance] = useState(0);
  const [deposit, setDeposit] = useState({
    apply: !!(invoice && invoice.depositToApply > 0),
    amount: invoice && invoice.depositToApply > 0 ? String(invoice.depositToApply) : "",
  });
  // What the sale is priced as (shipped or imported, the port, the title), so a
  // new car invoice can change it where its effect shows; the lines as they
  // were suggested, to tell whether staff have edited them since; and a
  // counter that rebuilds them after the quote changes.
  const [quote, setQuote] = useState(null);
  const [inUs, setInUs] = useState(false);
  const [suggested, setSuggested] = useState(null);
  const [linesVersion, setLinesVersion] = useState(0);
  // Bill-to, VIN and the pay link are usually right from the defaults; folded
  // away unless staff open them.
  const [showMore, setShowMore] = useState(false);
  const [draft, setDraft] = useState(() => ({
    kind: invoice ? invoice.kind : newKind || "vehicle",
    lineItems: invoice ? toDraftLines(invoice.lineItems) : [],
    issueDate: invoice ? invoice.issueDate || "" : "",
    dueDate: invoice ? invoice.dueDate || "" : "",
    billTo: {
      name: (invoice && invoice.billTo && invoice.billTo.name) || "",
      email: (invoice && invoice.billTo && invoice.billTo.email) || "",
      phone: (invoice && invoice.billTo && invoice.billTo.phone) || "",
      address: (invoice && invoice.billTo && invoice.billTo.address) || "",
    },
    vin: invoice ? invoice.vin || "" : "",
    externalRef: invoice ? invoice.externalRef || "" : "",
    total: invoice && isQuickBooks ? String(invoice.total) : "",
    payUrl: invoice ? invoice.payUrl || "" : "",
    notes: invoice ? invoice.notes || "" : "",
    adminNotes: invoice ? invoice.adminNotes || "" : "",
  }));

  // A new site invoice starts from the server's suggested lines. Keyed on the
  // scope's values, not the object, so a parent re-render does not reset the
  // lines being edited.
  const scopeKey = JSON.stringify(scope || {});
  useEffect(() => {
    if (invoice || isQuickBooks) return;
    getDealerInvoiceDefaults({ kind: newKind, template, ...JSON.parse(scopeKey) })
      .then((res) => {
        if (res?.message === "Success" && res.data) {
          const d = res.data;
          const lineItems = d.lineItems && d.lineItems.length ? toDraftLines(d.lineItems) : [blankLine()];
          setDraft((cur) => ({
            ...cur,
            lineItems,
            issueDate: d.issueDate || "",
            dueDate: d.dueDate || "",
            billTo: { ...cur.billTo, ...(d.billTo || {}) },
            vin: d.vin || "",
          }));
          setSuggested(lineItems);
          setQuote(d.quote ? { quoteType: d.quote.quoteType || "", port: d.quote.port || "", includeTitle: !!d.quote.includeTitle } : null);
          setInUs(!!(d.vehicle && d.vehicle.source === "current-inventory"));
          setTemplates(d.templates || null);
          setAgreed(d.agreedTotal ? { total: d.agreedTotal, car: d.agreedCarPrice } : null);
          setFeePresets(d.feePresets || []);
          setDepositBalance(Number(d.depositBalance) || 0);
          setDeposit({ apply: d.depositToApply > 0, amount: d.depositToApply > 0 ? String(d.depositToApply) : "" });
        } else {
          alert(errorMessage(res, "Could not load the invoice defaults."));
          setDraft((cur) => ({ ...cur, lineItems: [blankLine()] }));
        }
      })
      .catch(() => setDraft((cur) => ({ ...cur, lineItems: [blankLine()] })))
      .finally(() => setLoading(false));
  }, [invoice, isQuickBooks, newKind, scopeKey, template, linesVersion]);

  // An existing draft still needs the customer's balance for "Apply deposit".
  useEffect(() => {
    if (!invoice || invoice.source !== "site" || invoice.status !== "draft") return;
    const target = invoice.purchaseId ? { purchaseId: invoice.purchaseId } : invoice.inspectionId ? { inspectionId: invoice.inspectionId } : { userId: invoice.userId && (invoice.userId._id || invoice.userId) };
    getDealerInvoiceDefaults({ kind: invoice.kind, ...target })
      .then((res) => {
        if (res?.message === "Success" && res.data) {
          setDepositBalance(Number(res.data.depositBalance) || 0);
          setFeePresets(res.data.feePresets || []);
        }
      })
      .catch(() => {});
  }, [invoice]);

  const pickTemplate = (next) => {
    if (next === template) return;
    if (draft.lineItems.some((l) => l.description.trim()) && !window.confirm("Replace the lines with that template?")) return;
    setLoading(true);
    setTemplate(next);
  };

  // Shipped or imported, the port and the title are the sale's: a change here
  // is saved on the sale, and the suggested lines are rebuilt to it.
  const changeQuote = async (next) => {
    const edited = suggested && JSON.stringify(draft.lineItems) !== JSON.stringify(suggested);
    if (edited && !window.confirm("Save this on the sale and rebuild the lines? Your changes to the lines are replaced.")) return;
    setBusy(true);
    try {
      const res = await updateDealerAdminPurchase(scope.purchaseId, { quote: { ...next, quoteType: next.quoteType || null } });
      if (res?.message === "Purchase updated successfully") {
        setQuote(next);
        setLoading(true);
        setLinesVersion((v) => v + 1);
        if (onSaleChanged) onSaleChanged();
      } else {
        alert(errorMessage(res, "Could not save that on the sale."));
      }
    } catch (err) {
      alert("Could not save that on the sale.");
    } finally {
      setBusy(false);
    }
  };

  const editable = !saved || saved.status === "draft" || saved.source === "quickbooks";

  const total = useMemo(
    () => draft.lineItems.reduce((sum, l) => {
      const q = parseMoney(l.quantity);
      const p = parseMoney(l.unitPrice);
      return sum + (Number.isFinite(q) && Number.isFinite(p) && q !== null && p !== null ? q * p : 0);
    }, 0),
    [draft.lineItems]
  );

  const setLine = (i, key, value) =>
    setDraft((d) => ({ ...d, lineItems: d.lineItems.map((l, idx) => (idx === i ? { ...l, [key]: value } : l)) }));

  // Editing a part re-totals its line, so the price can never disagree with
  // the breakdown printed under it.
  const setBreakdown = (i, parts) =>
    setDraft((d) => ({
      ...d,
      lineItems: d.lineItems.map((l, idx) => (idx === i ? { ...l, breakdown: parts, unitPrice: parts.length ? String(breakdownSum(parts)) : l.unitPrice } : l)),
    }));

  const addLine = (line) =>
    setDraft((d) => ({
      ...d,
      // Fill the empty starter line rather than leaving it dangling above.
      lineItems: [...d.lineItems.filter((l) => l.description.trim() || l.unitPrice !== ""), { description: line.description, quantity: "1", unitPrice: String(line.unitPrice) }],
    }));

  // Inspection fee presets set the fee line (the first one, normally the only one).
  const setFee = (amount) =>
    setDraft((d) => {
      const lines = d.lineItems.length ? d.lineItems : [{ description: "Pre-purchase inspection", quantity: "1", unitPrice: "" }];
      const at = Math.max(0, lines.findIndex((l) => /inspection/i.test(l.description)));
      return { ...d, lineItems: lines.map((l, idx) => (idx === at ? { ...l, unitPrice: String(amount) } : l)) };
    });

  const canApplyDeposit = !isQuickBooks && draft.kind !== "deposit" && depositBalance > 0;
  const depositAmount = canApplyDeposit && deposit.apply ? Math.max(0, parseMoney(deposit.amount) || 0) : 0;

  const payload = () => {
    const body = {
      kind: draft.kind,
      issueDate: draft.issueDate,
      dueDate: draft.dueDate,
      payUrl: draft.payUrl.trim(),
      notes: draft.notes.trim(),
      adminNotes: draft.adminNotes.trim(),
    };
    if (isQuickBooks) {
      const amount = parseMoney(draft.total);
      if (!draft.externalRef.trim()) throw new Error("Enter the QuickBooks invoice number.");
      if (!(amount > 0)) throw new Error("Enter the invoice amount.");
      return { ...body, externalRef: draft.externalRef.trim(), total: amount };
    }
    const lineItems = draft.lineItems
      .filter((l) => l.description.trim() || l.unitPrice !== "")
      .map((l) => {
        const quantity = parseMoney(l.quantity);
        const unitPrice = parseMoney(l.unitPrice);
        if (!l.description.trim()) throw new Error("Every line needs a description.");
        if (!Number.isFinite(quantity) || quantity === null) throw new Error(`Check the quantity on "${l.description}".`);
        if (!Number.isFinite(unitPrice) || unitPrice === null) throw new Error(`Check the price on "${l.description}".`);
        const parts = (l.breakdown || []).filter((b) => b.label.trim() || b.amount !== "");
        if (!parts.length) return { description: l.description.trim(), quantity, unitPrice };
        const breakdown = parts.map((b) => {
          const value = parseMoney(b.amount);
          if (!b.label.trim()) throw new Error(`Every part of "${l.description}" needs a label.`);
          if (!Number.isFinite(value) || value === null) throw new Error(`Check the amount of "${b.label}".`);
          return { label: b.label.trim(), amount: value };
        });
        return { description: l.description.trim(), quantity, unitPrice: breakdownSum(breakdown.map((b) => ({ amount: String(b.amount) }))), breakdown };
      });
    if (depositAmount > depositBalance + 0.005) throw new Error(`The deposit balance is ${formatUsd(depositBalance)} - apply no more than that.`);
    if (depositAmount > lineItems.reduce((sum, l) => sum + l.quantity * l.unitPrice, 0) + 0.005) throw new Error("The deposit applied can't be more than the invoice total.");
    return {
      ...body,
      lineItems,
      depositToApply: depositAmount,
      vin: draft.vin.trim(),
      billTo: {
        name: draft.billTo.name.trim(),
        email: draft.billTo.email.trim(),
        phone: draft.billTo.phone.trim(),
        address: draft.billTo.address.trim(),
      },
    };
  };

  // Saves and returns the stored invoice (or null on failure).
  const save = async () => {
    let body;
    try {
      body = payload();
    } catch (err) {
      alert(err.message);
      return null;
    }
    setBusy(true);
    try {
      const res = saved
        ? await updateDealerAdminInvoice(saved._id, body)
        : await createDealerAdminInvoice({ ...body, source, ...scope });
      if (res?.message === "Invoice created" || res?.message === "Invoice updated") {
        setSaved(res.data);
        return res.data;
      }
      alert(errorMessage(res, "Could not save the invoice."));
      return null;
    } catch (err) {
      alert("Could not save the invoice.");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const saveAndClose = async () => {
    const result = await save();
    if (result) onDone(result);
  };

  const preview = async () => {
    const result = editable ? await save() : saved;
    if (!result) return;
    openPdf(() => getDealerAdminInvoicePdf(result._id)).catch((err) => alert(err.message));
  };

  const send = async () => {
    const result = await save();
    if (!result) return;
    const depositNote = result.depositToApply > 0 ? ` ${formatUsd(result.depositToApply)} of their deposit comes off it.` : "";
    if (!window.confirm(`Send this invoice to ${result.billTo && result.billTo.email}? It's numbered (with the car's VIN when there is one) and can no longer be edited.${depositNote}`)) return;
    setBusy(true);
    try {
      const res = await sendDealerAdminInvoice(result._id, true);
      if (res?.data && res.data.status !== "draft") {
        if (res.depositWarning) alert(res.depositWarning);
        else if (!res.emailed) alert("The invoice was issued, but the email did not go out. Use Resend from the invoice list.");
        // Their deposit covered the whole car invoice.
        if (res.orderConfirmed) alert(ORDER_CONFIRMED_NOTE);
        onDone(res.data);
      } else {
        alert(errorMessage(res, "Could not send the invoice."));
      }
    } catch (err) {
      alert("Could not send the invoice.");
    } finally {
      setBusy(false);
    }
  };

  const removeDraft = async () => {
    if (!saved) {
      onDone(null);
      return;
    }
    if (!window.confirm("Delete this draft?")) return;
    setBusy(true);
    try {
      const res = await deleteDealerAdminInvoice(saved._id);
      if (res?.message === "Draft deleted") onDone(null);
      else alert(errorMessage(res, "Could not delete the draft."));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className={styles.inlineForm}>Loading the suggested lines...</div>;

  const field = (label, key, props = {}) => (
    <div className={classes.formGroup}>
      <label className={classes.formLabel}>{label}</label>
      <input
        className={classes.formInput}
        value={draft[key]}
        disabled={!editable}
        onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
        {...props}
      />
    </div>
  );

  const billToSummary = [draft.billTo.name, draft.billTo.email, draft.billTo.address.split("\n")[0]].filter(Boolean).join(" · ");
  const footerTotal = isQuickBooks ? parseMoney(draft.total) || 0 : total;

  return (
    <div className={styles.inlineForm}>
      <div className={styles.editorHead}>
        <h3>{isQuickBooks ? "QuickBooks invoice" : saved && saved.number ? `Invoice ${saved.number}` : "New invoice"}</h3>
        {!isQuickBooks && (!saved || saved.status === "draft") && <span className={`${styles.chip} ${styles.chipMuted}`}>Draft</span>}
      </div>

      <div className={styles.grid3}>
        <div className={classes.formGroup}>
          <label className={classes.formLabel}>For</label>
          <select
            className={classes.formInput}
            value={draft.kind}
            disabled={!editable}
            onChange={(e) => setDraft((d) => ({ ...d, kind: e.target.value }))}
          >
            {/* No new inspection invoices: the fee goes on the final car invoice,
                or comes off the deposit refund (InspectionPanel). Older ones still show. */}
            {Object.entries(INVOICE_KIND_LABELS)
              .filter(([k]) => k !== "inspection" || draft.kind === "inspection")
              .map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </div>
        {field("Issue date", "issueDate", { type: "date" })}
        {field("Due date", "dueDate", { type: "date" })}
        {isQuickBooks && field("QuickBooks invoice no.", "externalRef", { placeholder: "e.g. 1043" })}
        {isQuickBooks && field("Amount (USD)", "total", { placeholder: "e.g. 2000", inputMode: "decimal" })}
      </div>
      {draft.kind === "deposit" && (
        <small className={styles.hint}>Payments on a deposit invoice are added to the customer's deposit balance.</small>
      )}
      {/* Billing a car in parts: the car now, shipping or the arrival costs later. */}
      {templates && !saved && draft.kind === "vehicle" && scope && scope.purchaseId && (
        <div className={classes.formGroup}>
          <label className={classes.formLabel} htmlFor="inv-template">Bill</label>
          <select id="inv-template" className={classes.formInput} value={template} onChange={(e) => pickTemplate(e.target.value)}>
            {Object.entries(templates).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </div>
      )}
      {quote && !saved && draft.kind === "vehicle" && scope && scope.purchaseId && (
        <>
          <div className={styles.grid3}>
            <QuoteFields quote={quote} inUs={inUs} disabled={busy} onChange={changeQuote} />
          </div>
          <small className={styles.hint} style={{ display: "block" }}>Changing these saves them on the sale and rebuilds the lines.</small>
        </>
      )}
      {agreed && !saved && draft.kind === "vehicle" && ["all", "car", "arrival"].includes(template) && (
        <small className={styles.hint}>
          {template === "all"
            ? `Built to add up to the agreed price on the sale, ${formatUsd(agreed.total, { cents: false })}, plus any inspection fee.`
            : `The car is priced at its share of the agreed ${formatUsd(agreed.total, { cents: false })}: ${formatUsd(agreed.car)}.`}
        </small>
      )}
      {editable && !isQuickBooks && draft.kind === "inspection" && feePresets.length > 0 && (
        <div className={styles.inlineRow}>
          <span className={styles.hint}>Inspection fee:</span>
          {feePresets.map((amount) => (
            <button key={amount} type="button" className={styles.btn} onClick={() => setFee(amount)}>{formatUsd(amount, { cents: false })}</button>
          ))}
          <span className={styles.hint}>$150 for most; more when the inspector travels.</span>
        </div>
      )}

      {!isQuickBooks && (
        <>
          <table className={styles.lineItems}>
            <thead>
              <tr>
                <th>Description</th>
                <th className={styles.qty}>Qty</th>
                <th className={styles.num}>Unit price</th>
                <th className={styles.num}>Amount</th>
                <th aria-label="Remove" />
              </tr>
            </thead>
            <tbody>
              {draft.lineItems.map((line, i) => {
                const q = parseMoney(line.quantity);
                const p = parseMoney(line.unitPrice);
                const amount = Number.isFinite(q) && Number.isFinite(p) && q !== null && p !== null ? q * p : null;
                const parts = line.breakdown || [];
                return (
                  // Lines have no stable id while being edited; the index is the identity.
                  <React.Fragment key={i}>
                  <tr>
                    <td>
                      <input value={line.description} disabled={!editable} onChange={(e) => setLine(i, "description", e.target.value)} aria-label="Description" />
                    </td>
                    <td className={styles.qty}>
                      <input value={line.quantity} disabled={!editable} onChange={(e) => setLine(i, "quantity", e.target.value)} aria-label="Quantity" inputMode="decimal" />
                    </td>
                    <td className={styles.num}>
                      {/* With a breakdown the price is its sum, edited part by part. */}
                      <input value={line.unitPrice} disabled={!editable || parts.length > 0} onChange={(e) => setLine(i, "unitPrice", e.target.value)} aria-label="Unit price" inputMode="decimal" title={parts.length ? "The sum of the breakdown below" : undefined} />
                    </td>
                    <td className={`${styles.num} ${styles.amountCell} ${amount < 0 ? styles.credit : ""}`}>{amount === null ? "" : formatUsd(amount)}</td>
                    <td className={styles.removeCell}>
                      {editable && (
                        <button
                          type="button"
                          className={styles.iconButton}
                          onClick={() => setDraft((d) => ({ ...d, lineItems: d.lineItems.filter((_, idx) => idx !== i) }))}
                          aria-label={`Remove ${line.description || "line"}`}
                          title="Remove line"
                        >
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                  {parts.map((part, j) => (
                    // Parts have no id either; position within the line is theirs.
                    <tr key={`${i}-${j}`} className={styles.breakdownRow}>
                      <td>
                        <input value={part.label} disabled={!editable} aria-label="Breakdown label" onChange={(e) => setBreakdown(i, parts.map((b, k) => (k === j ? { ...b, label: e.target.value } : b)))} />
                      </td>
                      <td />
                      <td className={styles.num}>
                        <input value={part.amount} disabled={!editable} aria-label={`Amount for ${part.label || "this part"}`} inputMode="decimal" onChange={(e) => setBreakdown(i, parts.map((b, k) => (k === j ? { ...b, amount: e.target.value } : b)))} />
                      </td>
                      <td />
                      <td className={styles.removeCell}>
                        {editable && (
                          <button type="button" className={styles.iconButton} aria-label={`Remove ${part.label || "part"}`} title="Remove from the breakdown" onClick={() => setBreakdown(i, parts.filter((_, k) => k !== j))}>
                            ×
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {editable && parts.length > 0 && (
                    <tr className={styles.breakdownRow}>
                      <td colSpan={5}>
                        <button type="button" className={styles.btnText} onClick={() => setBreakdown(i, [...parts, { label: "", amount: "" }])}>+ Add to the breakdown</button>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {editable && (
            <div className={styles.quickAdd}>
              <button type="button" className={styles.btnText} onClick={() => setDraft((d) => ({ ...d, lineItems: [...d.lineItems, blankLine()] }))}>
                + Add a line
              </button>
              {draft.kind !== "deposit" && QUICK_LINES.map((line) => (
                <button key={line.label} type="button" className={styles.btn} onClick={() => addLine(line)}>
                  + {line.label}{line.unitPrice ? ` ${formatUsd(line.unitPrice, { cents: false })}` : ""}
                </button>
              ))}
            </div>
          )}
          <small className={styles.hint} style={{ display: "block" }}>A negative price makes a credit line. The deposit comes off with "Apply deposit" below.</small>
          {editable && canApplyDeposit && (
            <div className={styles.inlineRow}>
              <label className={styles.checkboxRow} style={{ margin: 0 }}>
                <input type="checkbox" checked={deposit.apply} onChange={(e) => setDeposit((dep) => ({ apply: e.target.checked, amount: dep.amount || String(Math.min(depositBalance, Math.max(0, total))) }))} />
                Apply deposit on file ({formatUsd(depositBalance, { cents: false })})
              </label>
              {deposit.apply && (
                <input
                  className={classes.formInput}
                  style={{ width: 120, marginTop: 0 }}
                  value={deposit.amount}
                  aria-label="Deposit to apply"
                  inputMode="decimal"
                  onChange={(e) => setDeposit((dep) => ({ ...dep, amount: e.target.value }))}
                />
              )}
              {deposit.apply && <span className={styles.hint}>Taken from the balance when sent; printed as "Less: deposit applied".</span>}
            </div>
          )}
        </>
      )}

      <button type="button" className={styles.collapseToggle} onClick={() => setShowMore((v) => !v)} aria-expanded={showMore}>
        <span>
          {isQuickBooks
            ? <>Pay link &amp; notes</>
            : <>Bill to: <strong>{billToSummary || "not set"}</strong>{draft.vin ? ` · VIN ${draft.vin}` : ""}{editable && !draft.billTo.address.trim() ? " · no address yet (the customer can add it under Billing → Your details)" : ""}</>}
        </span>
        <span aria-hidden="true">{showMore ? "▲" : "▼"}</span>
      </button>

      {showMore && (
        <div>
          {!isQuickBooks && (
            <>
              <div className={styles.grid3}>
                {["name", "email", "phone"].map((key) => (
                  <div key={key} className={classes.formGroup}>
                    <label className={classes.formLabel}>{key === "name" ? "Bill to" : key === "email" ? "Email" : "Phone"}</label>
                    <input
                      className={classes.formInput}
                      value={draft.billTo[key]}
                      disabled={!editable}
                      onChange={(e) => setDraft((d) => ({ ...d, billTo: { ...d.billTo, [key]: e.target.value } }))}
                    />
                  </div>
                ))}
              </div>
              <div className={styles.grid2}>
                <div className={classes.formGroup}>
                  <label className={classes.formLabel}>Address</label>
                  <textarea
                    className={classes.formInput}
                    rows={2}
                    value={draft.billTo.address}
                    disabled={!editable}
                    onChange={(e) => setDraft((d) => ({ ...d, billTo: { ...d.billTo, address: e.target.value } }))}
                  />
                </div>
                {field("VIN / chassis no.", "vin")}
              </div>
            </>
          )}
          {field("Pay online link (optional, https)", "payUrl", { placeholder: "QuickBooks payment link" })}
        </div>
      )}

      <div className={styles.grid2}>
        <div className={classes.formGroup}>
          <label className={classes.formLabel}>{isQuickBooks ? "Note to customer" : "Notes printed on the invoice"}</label>
          <textarea className={classes.formInput} rows={2} value={draft.notes} disabled={!editable} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} />
        </div>
        <div className={classes.formGroup}>
          <label className={classes.formLabel}>Staff notes</label>
          <textarea className={classes.formInput} rows={2} value={draft.adminNotes} onChange={(e) => setDraft((d) => ({ ...d, adminNotes: e.target.value }))} />
        </div>
      </div>

      <div className={styles.editorFooter}>
        <div className={styles.footerTotal}>
          {isQuickBooks ? "Amount" : "Total"}
          <strong>{formatUsd(footerTotal)}</strong>
          {depositAmount > 0 && editable && (
            <span className={styles.footerSub}>
              Less deposit {formatUsd(depositAmount)} · {formatUsd(Math.max(0, footerTotal - depositAmount))} due
            </span>
          )}
        </div>
        {!isQuickBooks && editable && (
          <button type="button" className={styles.btnDangerText} disabled={busy} onClick={removeDraft}>
            {saved ? "Delete draft" : "Discard"}
          </button>
        )}
        <button type="button" className={styles.btnText} disabled={busy} onClick={() => onDone(saved)}>Close</button>
        {!isQuickBooks && (
          <button type="button" className={styles.btn} disabled={busy} onClick={preview}>Preview PDF</button>
        )}
        <button type="button" className={`${styles.btn} ${isQuickBooks || !editable ? styles.btnPrimary : ""}`} disabled={busy} onClick={saveAndClose}>
          {isQuickBooks ? "Save" : editable ? "Save draft" : "Save staff notes"}
        </button>
        {!isQuickBooks && editable && (
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} onClick={send}>
            Send to customer
          </button>
        )}
      </div>
    </div>
  );
};

export default InvoiceEditor;
